import React, { useEffect, useState, useContext } from "react";
import { useAuth } from "../../context/AuthContext";
import { login_zoom } from "../../api/AuthApi";
import { useNavigate } from "react-router";
import { GlobalContext } from "../../App";
import zoomSdk from "@zoom/appssdk";
import { configureZoomSdk } from "../../utilities/zoomSdkSetup";
import { Spin } from "antd";
import { Heading } from "../../components/ui/heading";
import { Text } from "../../components/ui/text";
import { getPostLoginPath } from "../../utilities/postLoginRoute";

const DummyFirstPage = () => {
  const { setToastOpen, setToastMessage, setToastSeverity } =
    useContext(GlobalContext);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const generateCodeVerifier = (length = 128) => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  };

  const generateCodeChallenge = async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  };

  const handleAuth = async (code, code_verifier) => {
    try {
      setLoading(true);
      setPageError("");

      const response = await login_zoom(code, code_verifier);
      const accessToken = response.access_token || response.token;

      if (!accessToken) {
        throw new Error("No access token received from server");
      }

      localStorage.setItem("refresh_token", response.refresh_token);
      sessionStorage.setItem("z_user_id", response.z_user_id);

      authLogin({
        ...response,
        access_token: accessToken,
        token: accessToken,
        user_type: response.user_type,
        z_user_id: response.z_user_id,
      });

      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Login successful!");

      const nextPath = getPostLoginPath(response.user_type);
      if (nextPath !== "/") {
        navigate(nextPath, { replace: true });
        return;
      }

      setPageError(
        `Login succeeded, but role "${response.user_type || "unknown"}" has no destination route.`
      );
    } catch (e) {
      console.error("Zoom login error:", e);
      const message =
        e.response?.data?.message ||
        e.response?.data?.error ||
        e.message ||
        "Login failed. Please try again.";

      setPageError(message);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      setPageError("");

      try {
        await configureZoomSdk();

        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        sessionStorage.setItem("code_verifier", codeVerifier);

        const authResult = await zoomSdk.authorize({
          codeChallenge,
          state: "dummy_state_123",
        });

        if (authResult.message !== "Success") {
          setPageError(
            authResult.message ||
              "Zoom authorization did not complete successfully."
          );
          setLoading(false);
          return;
        }

        zoomSdk.addEventListener("onAuthorized", async (event) => {
          await handleAuth(event.code, codeVerifier);
        });

        setLoading(false);
      } catch (error) {
        console.error("Zoom SDK initialization error:", error);
        setPageError(
          error?.message ||
            error?.code ||
            "Zoom SDK failed to initialize. Open this app from Zoom Contact Center."
        );
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  if (pageError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center space-y-4 p-6 text-center">
        <Heading>Unable to open operator panel</Heading>
        <Text>{pageError}</Text>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen">
      <Spin spinning={loading} fullscreen />
    </div>
  );
};

export default DummyFirstPage;
